import { FileExcelOutlined, SearchOutlined } from '@ant-design/icons';
import {
    Breadcrumb,
    Button,
    Col,
    Row,
    Space,
    Table,
    Tooltip,
    Form,
    Dropdown,
    DatePicker,
    Input,
    Menu,
    Checkbox,
    Spin,
    message, // Added for message.error
} from 'antd';
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as Back } from '../../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../../Images/ColumnList.svg';
import { ReactComponent as FilterIcon } from '../../../Images/FilterIcon.svg';
import { ReactComponent as PrintIcon } from '../../../Images/PrintIcon.svg';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWorkorderReport } from '../../../slices/report';
import eventBus from '../../../utils/eventBus';
// import { createActivityLog } from '../../../slices/activityLog'; // Uncomment if createActivityLog is defined in a separate slice
import * as XLSX from 'xlsx';
import logo from '../../../Images/alux-logo.png';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

dayjs.extend(customParseFormat);
const { RangePicker } = DatePicker;

function WorkOrderDateWiseReport() {
    let navigate = useNavigate();
    const dispatch = useDispatch();
    const { user: currentUser } = useSelector(state => state.auth);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [orderBy, setOrderBy] = useState('');
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [dateRange, setDateRange] = useState([
        dayjs().startOf('month'),
        dayjs(),
    ]);
    const { Workorderdata = [] } = useSelector(state => state.report);

    const fetchWorkOrdersReportData = useCallback(() => {
        const params = {
            ...(search && { search }),
            ...(orderBy && { ordering: orderBy }),
        };
        if (
            dateRange &&
            dateRange[0] &&
            dateRange[1] &&
            dayjs.isDayjs(dateRange[0]) &&
            dayjs.isDayjs(dateRange[1])
        ) {
            params.start_date = dateRange[0].format('YYYY-MM-DD');
            params.end_date = dateRange[1].format('YYYY-MM-DD');
        }
        try {
            dispatch(fetchWorkorderReport(params)).unwrap();
        } catch (error) {
            console.error('API fetch error:', error);
        } finally {
            setLoading(false);
        }
    }, [dispatch, search, orderBy, dateRange]);


    useEffect(() => {
        fetchWorkOrdersReportData();
    }, [fetchWorkOrdersReportData]);

    useEffect(() => {
        const handleReload = () => {
            fetchWorkOrdersReportData();
        };

        eventBus.on("financialYear:refresh", handleReload);

        return () => {
            eventBus.off("financialYear:refresh", handleReload);
        };
    }, [fetchWorkOrdersReportData]);

    const handleSearch = e => {
        setSearch(e.target.value);
        setPage(1);
    };

    const handleDateRangeChange = dates => {
        if (dates) {
            setDateRange(dates);
        }
    };

    const resetFilter = () => {
        setSearch('');
        setPage(1);
        setOrderBy('');
    };

    let dataSource = [];
    let rowIndex = 0;
    Workorderdata.forEach((customer, cIdx) => {
        let customerTotal = 0;
        customer.orders.forEach((order, oIdx) => {
            const netWeight = parseFloat(order.net_weight || 0);
            customerTotal += netWeight;
            dataSource.push({
                key: `${cIdx}-${oIdx}`,
                sr_no: ++rowIndex,
                party: customer.customer_name,
                date: order.order_date,
                workOrder: order.work_order_no,
                totalTonnage: order.net_weight,
                isSubtotal: false,
                partyRowSpan: oIdx === 0 ? customer.orders.length : 0,
            });
        });

        dataSource.push({
            key: `subtotal-${cIdx}`,
            isSubtotal: true,
            party: '',
            date: '',
            workOrder: '',
            totalTonnage: customerTotal,
        });
    });

    const columns = [
        {
            title: 'Sr No.',
            dataIndex: 'sr_no',
            key: 'sr_no',
        },
        {
            title: 'Customer',
            dataIndex: 'party',
            key: 'party',
            render: (value, record) => {
                if (record.isSubtotal) {
                    return {
                        children: null,
                        props: {
                            colSpan: 0,
                        },
                    };
                }
                return {
                    children: value,
                    props: {
                        rowSpan: record.partyRowSpan,
                    },
                };
            }
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (text, record) => {
                if (!record.isSubtotal) {
                    const date = new Date(text);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return `${day}-${month}-${year}`;
                }
            },
        },
        {
            title: 'Work Order',
            dataIndex: 'workOrder',
            key: 'workOrder',
        },
        {
            title: 'Total Tonnage',
            dataIndex: 'totalTonnage',
            key: 'totalTonnage',
            align: 'right',
            render: (value, record) => {
                if (record.isSubtotal) {
                    return {
                        children: <b>{parseFloat(value).toFixed(3)} KG</b>,
                        props: {
                            colSpan: 5,
                        },
                    };
                }
                return `${parseFloat(value).toFixed(3)} Kg`;
            },
        },
    ];

    const [checkedList, setCheckedList] = useState(columns.map(col => col.key));
    const filteredColumns = columns.filter(col => checkedList.includes(col.key));
    const handleColumnVisibilityChange = values => setCheckedList(values);

    const columnMenu = (
        <Menu className="packagelistdropdown">
            <Menu.Item>
                <Checkbox.Group value={checkedList} onChange={handleColumnVisibilityChange}>
                    <Row>
                        {columns.map(col => (
                            <Col span={12} key={col.key}>
                                <Checkbox value={col.key} disabled={col.key === 'sr_no'}>
                                    {col.title}
                                </Checkbox>
                            </Col>
                        ))}
                    </Row>
                </Checkbox.Group>
            </Menu.Item>
        </Menu>
    );

    const handlePrintDownloadAll = async () => {
        setLoading(true);
        try {
            const params = {
                page: 1,
                pagesize: 10000,
                ...(search && { search }),
                ...(orderBy && { ordering: orderBy }),
                ...(dateRange && dateRange[0] && dateRange[1] && {
                    start_date: dateRange[0].format('YYYY-MM-DD'),
                    end_date: dateRange[1].format('YYYY-MM-DD'),
                }),
            };
            const response = await dispatch(fetchWorkorderReport(params)).unwrap();
            const allData = response?.data || [];
            if (allData.length === 0) {
                message.error('No data available to print.');
                setLoading(false);
                return;
            }
            const pdf = new jsPDF('p', 'pt', 'a4');
            pdf.setFont('times');
            const pageWidth = pdf.internal.pageSize.width;
            const pageHeight = pdf.internal.pageSize.height;
            const footerHeight = 40;
            const margin = { top: 80, left: 20, right: 20, bottom: footerHeight + 40 };
            pdf.setFontSize(14);
            pdf.text('Work Order Report', pageWidth / 2, 40, { align: 'center' });
            const pdfColumns = [
                { header: 'Sr No.', dataKey: 'sr_no', field: 'sr_no' },
                { header: 'Customer', dataKey: 'party', field: 'party' },
                { header: 'Date', dataKey: 'date', field: 'date' },
                { header: 'Work Order', dataKey: 'workOrder', field: 'workOrder' },
                { header: 'Total Tonnage', dataKey: 'totalTonnage', field: 'totalTonnage' },
            ];
            const pdffilteredColumns = pdfColumns.filter(column =>
                checkedList.includes(column.dataKey)
            );

            let pdfRows = [];
            let srNo = 1;
            let customerMergeInfo = {}; // Store customer merge information

            allData.forEach((customer, cIdx) => {
                let customerTotal = 0;
                const orderCount = customer.orders.length;
                const customerStartIndex = pdfRows.length;

                // Store customer merge info for this group (excluding subtotal row)
                if (orderCount > 1) {
                    customerMergeInfo[customerStartIndex] = {
                        value: customer.customer_name,
                        rowCount: orderCount, // Only the actual order rows, not subtotal
                        endIndex: customerStartIndex + orderCount - 1
                    };
                }

                customer.orders.forEach((order, oIdx) => {
                    const netWeight = parseFloat(order.net_weight || 0);
                    customerTotal += netWeight;
                    const row = {
                        sr_no: srNo++,
                        party: oIdx === 0 ? customer.customer_name : '', // Show only on first row
                        date: order.order_date ? dayjs(order.order_date).format('DD-MM-YYYY') : 'N/A',
                        workOrder: order.work_order_no || 'N/A',
                        totalTonnage: `${netWeight.toFixed(3)} Kg`,
                        isSubtotal: false,
                        partyRowSpan: oIdx === 0 ? orderCount : 0,
                    };
                    pdfRows.push(row);
                });

                // Add subtotal row
                pdfRows.push({
                    sr_no: '',
                    party: '',
                    date: '',
                    workOrder: 'Subtotal',
                    totalTonnage: `${customerTotal.toFixed(3)} Kg`,
                    isSubtotal: true,
                });
            });

            // Track drawn merges to avoid redrawing
            let drawnCustomerMerges = new Set();

            pdf.autoTable({
                columns: pdffilteredColumns,
                body: pdfRows,
                startY: margin.top,
                theme: 'grid',
                margin: margin,
                rowPageBreak: 'auto',
                styles: {
                    font: 'times',
                    halign: 'center',
                    overflow: 'linebreak',
                    cellPadding: 3.5,
                    lineWidth: 0.5,
                    lineColor: [0, 0, 0],
                    textColor: [0, 0, 0],
                },
                headStyles: {
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                    lineWidth: 0.5,
                    lineColor: [0, 0, 0],
                    fillColor: false,
                },
                bodyStyles: {
                    lineWidth: 0.5,
                    lineColor: [0, 0, 0],
                },
                alternateRowStyles: {
                    fillColor: false,
                },
                columnStyles: {
                    totalTonnage: { halign: 'right' },
                    workOrder: { cellWidth: pdffilteredColumns.length <= 3 ? 'auto' : 100 },
                    party: {
                        halign: 'center',
                        valign: 'middle'
                    },
                },
                tableLineColor: [0, 0, 0],

                // Handle subtotal row colspan
                didParseCell: (data) => {
                    if (data.row.raw.isSubtotal && data.column.dataKey !== 'totalTonnage') {
                        data.cell.text = [''];
                        data.cell.colSpan = pdffilteredColumns.length - 1;
                    }
                },

                // Hide content of merged customer cells (except first)
                willDrawCell: (data) => {
                    if (data.column.dataKey === 'party' && !data.row.raw.isSubtotal) {
                        const rowIndex = data.row.index;

                        // Check if this is part of a merge group but not the first row
                        for (const [startIdx, info] of Object.entries(customerMergeInfo)) {
                            const start = parseInt(startIdx);
                            if (rowIndex > start && rowIndex <= info.endIndex) {
                                // This is a subsequent row in a merge group - completely hide it
                                return false;
                            }
                        }
                    }
                    return true;
                },

                // Draw the merged customer cells
                didDrawCell: (data) => {
                    if (data.column.dataKey === 'party' && !data.row.raw.isSubtotal) {
                        const rowIndex = data.row.index;
                        const mergeKey = `customer_${rowIndex}`;

                        // Check if this row starts a merge group
                        if (customerMergeInfo[rowIndex] && !drawnCustomerMerges.has(mergeKey)) {
                            drawnCustomerMerges.add(mergeKey);

                            const info = customerMergeInfo[rowIndex];
                            const { x, y, width, height } = data.cell;
                            const totalHeight = height * info.rowCount;

                            // Clear the merged area
                            pdf.setFillColor(255, 255, 255);
                            pdf.rect(x + 0.25, y + 0.25, width - 0.5, totalHeight - 0.5, 'F');

                            // Draw the merged cell border
                            pdf.setDrawColor(0, 0, 0);
                            pdf.setLineWidth(0.5);
                            pdf.rect(x, y, width, totalHeight, 'S');

                            // Add the customer name text centered in the merged cell
                            pdf.setFontSize(10);
                            pdf.setFont('times', 'normal');
                            pdf.setTextColor(0, 0, 0);

                            const textX = x + width / 2;
                            const textY = y + totalHeight / 2;

                            pdf.text(info.value, textX, textY, {
                                align: 'center',
                                baseline: 'middle'
                            });
                        }

                        // For subsequent rows in a merge group, clear their individual borders
                        for (const [startIdx, info] of Object.entries(customerMergeInfo)) {
                            const start = parseInt(startIdx);
                            if (rowIndex > start && rowIndex <= info.endIndex) {
                                const { x, y, width, height } = data.cell;
                                // Clear the individual cell border
                                pdf.setFillColor(255, 255, 255);
                                pdf.rect(x, y, width, height, 'F');

                                // Redraw only the side borders that should remain
                                pdf.setDrawColor(0, 0, 0);
                                pdf.setLineWidth(0.5);

                                // Left border
                                pdf.line(x, y, x, y + height);
                                // Right border
                                pdf.line(x + width, y, x + width, y + height);

                                break;
                            }
                        }
                    }
                },

                didDrawPage: (data) => {
                    const pageNumber = pdf.internal.getNumberOfPages();
                    const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
                    const currentDateTime = dayjs().format('DD/MM/YYYY hh:mm:ss A');
                    const footerY = pdf.internal.pageSize.height - footerHeight / 2;
                    pdf.setFillColor(255, 255, 255);
                    pdf.rect(margin.left, footerY - 15, pageWidth - margin.left - margin.right, footerHeight, 'F');
                    pdf.setFontSize(10);
                    pdf.text(currentDateTime, margin.left, footerY);
                    pdf.text(`Page ${currentPage}`, pageWidth - margin.right - 70, footerY);
                    if (currentPage === 1) {
                        pdf.addImage(logo, 'PNG', pageWidth - 160, 10, 150, 32);
                    }
                },
            });

            pdf.autoPrint();
            const pdfBlob = pdf.output('blob');
            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.right = '0px';
            iframe.style.bottom = '0px';
            iframe.style.width = '1px';
            iframe.style.height = '1px';
            iframe.style.border = 'none';
            iframe.src = URL.createObjectURL(pdfBlob);
            document.body.appendChild(iframe);
            iframe.onload = () => {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
            };
        } catch (error) {
            console.error('Print error:', error);
            message.error('Failed to generate print.');
        } finally {
            setLoading(false);
        }
    };

    const handleExcelExport = () => {
        const headers = ['Sr No.', 'Party', 'Date', 'Work Order', 'Total Tonnage'];
        const wsData = [headers];
        const merges = [];
        let rowCounter = 1;
        let srNo = 1;

        Workorderdata.forEach(customer => {
            const partyName = customer.customer_name;
            const orderCount = customer.orders.length;

            customer.orders.forEach((order, idx) => {
                const row = [
                    srNo++,
                    partyName,
                    dayjs(order.order_date).format('DD-MM-YYYY'),
                    order.work_order_no,
                    Number(order?.net_weight),
                ];
                wsData.push(row);

                if (idx === 0 && orderCount > 1) {
                    merges.push({
                        s: { r: rowCounter, c: 1 },
                        e: { r: rowCounter + orderCount - 1, c: 1 },
                    });
                }

                rowCounter++;
            });
        });

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!merges'] = merges;
        ws['!cols'] = [
            { wch: 8 },
            { wch: 20 },
            { wch: 15 },
            { wch: 20 },
            { wch: 15 },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Work Order Report');
        XLSX.writeFile(wb, `WorkOrderReport_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    };

    return (
        <div className='mainwrap'>
            <div className="sticyaction">
                <div className="sticky-header">
                    <Row className="pagenamerow mb-0 " justify="space-between" align="middle">
                        <Col>
                            <h2 style={{ margin: 0 }}>Work Order Report</h2>
                            <div className="bredcrumbwrp">
                                <Link className="back" onClick={() => navigate(-1)}>
                                    <Back /> BACK
                                </Link>
                                <Breadcrumb
                                    items={[
                                        { title: <Link to="/dashboard">Home</Link> },
                                        { title: 'Work Order Report' },
                                    ]}
                                />
                            </div>
                        </Col>
                        <Col>
                            <RangePicker
                                value={dateRange}
                                format="DD-MM-YYYY"
                                onChange={handleDateRangeChange}
                                style={{ width: '300px', marginRight: '10px' }}
                                presets={{
                                    Today: [dayjs(), dayjs()],
                                    'This Week': [dayjs().startOf('week'), dayjs()],
                                    'This Month': [dayjs().startOf('month'), dayjs()],
                                    'Last Month': [
                                        dayjs().subtract(1, 'month').startOf('month'),
                                        dayjs().subtract(1, 'month').endOf('month'),
                                    ],
                                }}
                            />
                            <Space size="small">
                                <Input
                                    placeholder="Search"
                                    className="searchbox"
                                    prefix={<SearchOutlined />}
                                    value={search}
                                    style={{ width: 190 }}
                                    onChange={handleSearch}
                                />
                                <Tooltip title="Filter">
                                    <Button
                                        type="primary"
                                        disabled={!search && !orderBy}
                                        className="iconlink"
                                        onClick={resetFilter}
                                    >
                                        <FilterIcon />
                                    </Button>
                                </Tooltip>
                                {currentUser.permissions.permissions.workorder.includes(
                                    'print_workorder_report',
                                ) && (
                                        <Tooltip title="Print">
                                            <Button type="primary" className="iconlink" onClick={handlePrintDownloadAll}>
                                                <PrintIcon />
                                            </Button>
                                        </Tooltip>
                                    )}
                                {currentUser.permissions.permissions.workorder.includes(
                                    'download_workorder_report_excel_copy',
                                ) && (
                                        <Tooltip title="Export to Excel">
                                            <Button type="primary" className="iconlink" onClick={handleExcelExport}>
                                                <FileExcelOutlined />
                                            </Button>
                                        </Tooltip>
                                    )}
                                <div className="gridiconswrap">
                                    <Tooltip placement="topLeft" title="Column Filter Options">
                                        <Dropdown
                                            overlay={columnMenu}
                                            trigger={['click']}
                                            placement="bottomRight"
                                            open={dropdownVisible}
                                            onOpenChange={setDropdownVisible}
                                        >
                                            <Button type="primary" icon={<ColumnList />} />
                                        </Dropdown>
                                    </Tooltip>
                                </div>
                            </Space>
                        </Col>
                    </Row>
                </div>
            </div>
            <Spin spinning={loading}>
                <div id="printable-table" className='tablewrp'>
                    <Table
                        dataSource={dataSource}
                        columns={filteredColumns}
                        pagination={false}
                        bordered
                        style={{ backgroundColor: '#fff' }}
                    />
                </div>
            </Spin>
        </div>
    );
}

export default WorkOrderDateWiseReport;